import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineSpinner } from "@/components/common/Loading";
import { ShieldCheck, ShieldAlert, Copy, CheckCircle2 } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";

export default function Security() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startSetup = async () => {
    setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get("setup") === "1" && user && !user.mfa_enabled) startSetup();
    // eslint-disable-next-line
  }, [user]);

  const enable = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      await refreshUser();
      setSetup(null); setCode("");
      toast.success("Two-factor authentication enabled");
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    try { await api.post("/auth/mfa/disable"); await refreshUser(); toast.success("MFA disabled"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const qrUrl = setup ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_uri)}` : null;

  return (
    <div>
      <PageHeader title="Security & MFA" description="Protect your account with two-factor authentication." />
      <div className="max-w-2xl space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-4">
            {user?.mfa_enabled
              ? <ShieldCheck className="w-8 h-8 text-emerald-500 shrink-0" />
              : <ShieldAlert className="w-8 h-8 text-amber-500 shrink-0" />}
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">Authenticator app (TOTP)</h3>
              <p className="text-sm text-slate-500 mt-1">
                {user?.mfa_enabled ? "MFA is currently enabled on your account."
                  : user?.mfa_required ? "Your role requires MFA. Set it up to fully secure your account."
                  : "Add an extra layer of security using an authenticator app."}
              </p>
              <div className="mt-2" data-testid="mfa-status">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${user?.mfa_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {user?.mfa_enabled ? "Enabled" : (user?.mfa_required ? "Required" : "Disabled")}
                </span>
              </div>
            </div>
            <div>
              {!user?.mfa_enabled && !setup && (
                <Button onClick={startSetup} disabled={loading} data-testid="mfa-setup-start" className="bg-[#006090] hover:bg-[#004E75]">
                  {loading ? <InlineSpinner /> : "Set up MFA"}
                </Button>
              )}
              {user?.mfa_enabled && !user?.mfa_required && (
                <Button variant="outline" onClick={disable} data-testid="mfa-disable">Disable</Button>
              )}
            </div>
          </div>

          {setup && (
            <form onSubmit={enable} className="mt-6 border-t border-slate-100 pt-6 grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">1. Scan this QR code</p>
                {qrUrl && <img src={qrUrl} alt="MFA QR" className="rounded-md border border-slate-200" data-testid="mfa-qr" />}
                <div className="mt-3">
                  <Label className="text-xs text-slate-500">Or enter this key manually</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs bg-slate-100 rounded px-2 py-1 break-all">{setup.secret}</code>
                    <button type="button" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }}>
                      <Copy className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">2. Enter the 6-digit code</p>
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-enable-code">
                  <InputOTPGroup>{[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                </InputOTP>
                {error && <p className="text-sm text-rose-600 mt-2" data-testid="mfa-error">{error}</p>}
                <Button type="submit" disabled={loading || code.length < 6} data-testid="mfa-enable-submit"
                  className="mt-4 w-full bg-[#0093D6] hover:bg-[#0077B3]">
                  {loading ? <InlineSpinner /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Verify & enable</>}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
